import styled from '@emotion/styled';
import { VisibilityLabels, VisibilityStates } from '@racemap/utilities/consts/events';
import { Flex, Radio } from 'antd';
import { useState } from 'react';
import {
  IconVisibilityArchived,
  IconVisibilityDraft,
  IconVisibilityListed,
  IconVisibilityPublic,
  IconVisibilityUnlisted,
} from './BasicComponents/Icons';

type VisibilityTarget = 'event' | 'contestGroup' | 'stageGroup';

export default function VisibilitySection({
  value,
  onChange = () => {},
  authorized,
  possibleStates = Object.values(VisibilityStates).filter(
    (e) => e !== VisibilityStates.PUBLIC && e !== VisibilityStates.ARCHIVED,
  ),
  target = 'event',
  disabled = false,
}: {
  value: VisibilityStates;
  onChange?: (arg0: VisibilityStates) => void;
  authorized: boolean;
  possibleStates?: Array<VisibilityStates>;
  target?: VisibilityTarget;
  disabled?: boolean;
}) {
  const [hover, setHover] = useState<VisibilityStates | null | undefined>(null);
  const helpValue = hover != null ? hover : value;
  const IconVisibility = {
    DRAFT: <IconVisibilityDraft />,
    UNLISTED: <IconVisibilityUnlisted />,
    LISTED: <IconVisibilityListed />,
    ARCHIVED: <IconVisibilityArchived />,
    PUBLIC: <IconVisibilityPublic />,
  };

  return (
    <>
      <Radio.Group
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        optionType="button"
      >
        {possibleStates.map((state) => (
          <Radio.Button
            key={state}
            value={state}
            disabled={disabled || state === VisibilityStates.DRAFT || !authorized}
            onMouseEnter={() => setHover(state)}
            onMouseLeave={() => setHover(null)}
          >
            <Flex align="center" gap={4}>
              {IconVisibility[state]} {VisibilityLabels[state]}
            </Flex>
          </Radio.Button>
        ))}
      </Radio.Group>
      <HelpText>{createHelpText(helpValue, authorized, target)}</HelpText>
    </>
  );
}

const HelpText = styled.div`
  font-size: 11px;
  padding-top: 8px;
`;

const createHelpText = (
  visibilityState: VisibilityStates,
  authorized: boolean,
  target: VisibilityTarget,
) => {
  // Contest and stage groups share the same wording outside of DRAFT.
  const isGroupTarget = target === 'contestGroup' || target === 'stageGroup';

  switch (visibilityState) {
    case VisibilityStates.DRAFT:
      if (authorized && target === 'stageGroup')
        return (
          <>
            Set the visibility status for your stage event when the Base Price of each stage is
            paid.
          </>
        );
      if (authorized && target === 'contestGroup')
        return (
          <>
            Set the visibility status for your contest group when the Base Price of each contest is
            paid.
          </>
        );
      if (authorized)
        return (
          <>
            Your Tracking Map is currently in a draft state, but already functioning. Make sure to
            set it to <strong>Unlisted, Listed or Open</strong> in order to activate your Tracking
            Map.
          </>
        );
      return (
        <>
          Test geolocation and live tracking in draft state of your event for free. You can use
          add-ons and adapt the settings to your use case.
        </>
      );
    case VisibilityStates.LISTED:
      if (isGroupTarget)
        return (
          <>
            We help you reach a large audience. Your Group/Stage Map is shown in our public event
            lists on the{' '}
            <a href="/events" target="_blank" rel="noopener noreferrer">
              Racemap website
            </a>{' '}
            and in the Racemap App. Additionally, you can share the link or embed the tracking map
            on other websites in order to expand your audience.
          </>
        );
      return (
        <>
          We help you reach a large audience. Your Tracking Map is shown in our public event lists
          on the{' '}
          <a href="/events" target="_blank" rel="noopener noreferrer">
            Racemap website
          </a>{' '}
          and in the Racemap App. Additionally, you can share the link or embed the tracking map on
          other websites in order to expand your audience.
        </>
      );
    case VisibilityStates.UNLISTED:
      if (isGroupTarget)
        return (
          <>
            Your Group/Stage Map is <strong>not</strong> listed in our public event lists on the{' '}
            <a href="/events" target="_blank" rel="noopener noreferrer">
              Racemap website
            </a>{' '}
            or in the Racemap App. Only people with access to the link will be able to view it. You
            can share the link and embed the Group/Stage Map on other websites in order to reach
            your target audience.
          </>
        );
      return (
        <>
          Your event is <strong>not</strong> listed in our public event lists on the{' '}
          <a href="/events" target="_blank" rel="noopener noreferrer">
            Racemap website
          </a>{' '}
          or in the Racemap App. Only people with access to the link will be able to view it. You
          can share the link and embed the tracking map on other websites in order to reach your
          target audience.
        </>
      );
    case VisibilityStates.ARCHIVED:
      if (isGroupTarget)
        return (
          <>
            There will no loads charged for an archived event. An archived event will not load the
            Group/Stage map, Leaderboard, Data API or display any other information. You can restore
            the event at any time.
          </>
        );
      return (
        <>
          There will no loads charged for an archived event. An archived event will not load the
          Tracking map, Leaderboard, Data API or display any other information. You can restore the
          event at any time.
        </>
      );
    case VisibilityStates.PUBLIC:
      return (
        <>
          We help you reach a large audience. Your Tracking Map is shown in our public event lists
          on the{' '}
          <a href="/events" target="_blank" rel="noopener noreferrer">
            Racemap website
          </a>{' '}
          and in the Racemap App. Additionally, you can share the link or embed the Tracking Map on
          another website in order to expand your audience. Everybody with the Racemap App will be
          able to join your Tracking Map. You may also add participants manually, with Keys or with
          the Passcode.
        </>
      );
    default:
      return <div />;
  }
};
